/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageCircle, CalendarClock, AlertTriangle, CreditCard, ArrowRight } from "lucide-react";

const SMS_TEMPLATES = {
  pickup: (r) => `Hi ${r.customer_name?.split(" ")[0]}, reminder from Fair Rideshare LLC: your pickup for the ${r.vehicle_label} is tomorrow at ${new Date(r.when).toLocaleString()}. Please bring your driver's license, insurance, and payment. Reply if you need to reschedule.`,
  return: (r) => `Hi ${r.customer_name?.split(" ")[0]}, friendly reminder from Fair Rideshare LLC: your rental ${r.vehicle_label} is due back tomorrow at ${new Date(r.when).toLocaleString()}. Balance due: ${money(r.balance_due || 0)}.`,
  overdue: (r) => `Hi ${r.customer_name?.split(" ")[0]}, your Fair Rideshare LLC rental (${r.vehicle_label}) is overdue. Please return it immediately or contact us to avoid additional fees + repossession.`,
  payment: (r) => `Hi ${r.customer_name?.split(" ")[0]}, this is Fair Rideshare LLC — your outstanding balance is ${money(r.balance_due)}. Please send via Zelle / Cash App or in person. Reply when paid. Thanks!`,
};

function smsHref(phone, body) {
  const num = (phone || "").replace(/[^\d+]/g, "");
  const sep = /iPhone|iPad|Mac/i.test(navigator.userAgent) ? "&" : "?";
  return `sms:${num}${sep}body=${encodeURIComponent(body)}`;
}

function Section({ title, icon: Icon, color, items, kind, nav }) {
  if (!items?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={color} />
        <h3 className="font-heading font-semibold text-sm">{title}</h3>
        <span className="pill pill-gray text-[10px]">{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.reservation_id} className="flex items-center gap-2 py-2 border-t border-gray-100 first:border-t-0">
            <button onClick={() => nav(`/admin/reservations/${r.reservation_id}`)} className="flex-1 text-left">
              <div className="text-sm font-medium">{r.customer_name}</div>
              <div className="text-xs text-gray-500">{r.vehicle_label}{r.when ? ` · ${fmtDateTime(r.when)}` : ""}{r.balance_due ? ` · bal ${money(r.balance_due)}` : ""}</div>
            </button>
            {r.phone && (
              <a
                href={smsHref(r.phone, SMS_TEMPLATES[kind](r))}
                data-testid={`reminder-text-${r.reservation_id}`}
                className="inline-flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium px-2.5 py-1.5 rounded-md"
              >
                <MessageCircle size={12} /> Text
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RemindersWidget() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/reminders").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const total = (data.pickups?.length || 0) + (data.returns?.length || 0) + (data.overdue?.length || 0) + (data.balances?.length || 0);
  if (total === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800" data-testid="reminders-empty">
        ✓ No reminders to send today. All caught up.
      </div>
    );
  }
  return (
    <div data-testid="reminders-widget">
      <Section title="Pickup tomorrow" icon={CalendarClock} color="text-blue-600" items={data.pickups} kind="pickup" nav={nav} />
      <Section title="Return tomorrow" icon={CalendarClock} color="text-amber-600" items={data.returns} kind="return" nav={nav} />
      <Section title="Overdue" icon={AlertTriangle} color="text-red-600" items={data.overdue} kind="overdue" nav={nav} />
      <Section title="Outstanding balance" icon={CreditCard} color="text-amber-700" items={data.balances} kind="payment" nav={nav} />
    </div>
  );
}
